import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import { toast } from 'react-toastify';

import api from '../../services/api';
import {
  DetailField,
  FormSection,
  PageHeader,
  PageLayout,
  SearchableSelect,
  SectionCard,
} from '../../components/ui';

const DEFAULT_CENTER = { lat: 24.608768, lng: 46.678016 };
const DEFAULT_ZOOM = 12;

const punchTypes = [
  'CheckIn',
  'CheckOut',
  'BreakIn',
  'BreakOut',
  'OverTimeIn',
  'OverTimeOut',
  'MealIn',
  'MealOut',
];

const inputTypes = [
  'Fingerprint',
  'Face',
  'Card',
  'Palm',
  'Vein',
  'Retina',
  'Password',
  'GPS',
];

const toDateTimeLocal = () => new Date().toISOString().slice(0, 16);

export default function NewAttendancePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const [employeeCode, setEmployeeCode] = useState(() => localStorage.getItem('employee_code') || '');
  const [deviceStgid, setDeviceStgid] = useState('WEB-01');
  const [logTime, setLogTime] = useState(toDateTimeLocal);
  const [type, setType] = useState('CheckIn');
  const [inputType, setInputType] = useState('GPS');
  const [temperature, setTemperature] = useState('');
  const [mask, setMask] = useState(false);
  const [operationId, setOperationId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [lat, setLat] = useState(String(DEFAULT_CENTER.lat));
  const [lng, setLng] = useState(String(DEFAULT_CENTER.lng));

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let active = true;
    setProjectsLoading(true);
    setProjectsError('');

    api
      .get('/projects')
      .then((res) => {
        if (!active) return;
        setProjects(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (!active) return;
        console.error('Failed to fetch projects:', err);
        setProjects([]);
        setProjectsError('Projects could not be loaded. You can still submit without a project.');
      })
      .finally(() => {
        if (active) setProjectsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const selectedProject = useMemo(
    () => projects.find((project) => String(project.id) === String(projectId)) || null,
    [projectId, projects]
  );

  const mapLat = parseFloat(lat) || DEFAULT_CENTER.lat;
  const mapLng = parseFloat(lng) || DEFAULT_CENTER.lng;

  const completionPct = Math.round(
    ((employeeCode ? 1 : 0) + (deviceStgid ? 1 : 0) + (logTime ? 1 : 0) + (type ? 1 : 0) + (inputType ? 1 : 0)) / 5 * 100
  );

  const handleMapClick = (event) => {
    const latLng = event?.detail?.latLng;
    const nextLat = latLng?.lat ?? event?.latLng?.lat?.();
    const nextLng = latLng?.lng ?? event?.latLng?.lng?.();
    if (nextLat === undefined || nextLng === undefined) return;
    setLat(String(nextLat));
    setLng(String(nextLng));
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setFormError('Geolocation is not supported in this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        setFormError('');
      },
      (err) => {
        console.error(err);
        setFormError('Could not get your current location.');
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    try {
      const rawPayload = JSON.stringify({
        latitude: Number(lat),
        longitude: Number(lng),
      });

      await api.post('/attendances', {
        employee_code: employeeCode,
        project_id: projectId ? Number(projectId) : null,
        device_stgid: deviceStgid,
        log_time: logTime,
        type,
        input_type: inputType,
        temperature: temperature ? Number(temperature) : null,
        mask: Boolean(mask),
        operation_id: operationId || null,
        raw_payload: rawPayload,
      });

      toast.success('Attendance saved.');
      navigate('/');
    } catch (err) {
      console.error('Error saving attendance', err);
      setFormError(err.response?.data?.error || err.response?.data?.message || 'Failed to save attendance.');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.035) : alpha('#ffffff', 0.86),
    },
  };

  return (
    <PageLayout backgroundAccent maxWidth="xl">
      <PageHeader
        overline="Attendance"
        title="New Attendance"
        subtitle="Create a web attendance record with project, device, punch type, and location context."
        variant="dashboard"
        actions={
          <Button variant="outlined" onClick={() => navigate('/')}>
            Cancel
          </Button>
        }
      />

      <Grid container spacing={2.2}>
        <Grid item xs={12} lg={8}>
          <FormSection
            title="Attendance Record"
            description="Required fields are used exactly as the current attendance API expects."
            tone="dashboard"
          >
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {formError ? <Alert severity="error">{formError}</Alert> : null}
                {projectsError ? <Alert severity="warning">{projectsError}</Alert> : null}

                <SectionCard tone="dashboard">
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeRoundedIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
                          Submission Readiness
                        </Typography>
                      </Stack>
                      <Chip size="small" color={completionPct === 100 ? 'success' : 'warning'} label={`${completionPct}% complete`} variant="outlined" />
                    </Stack>
                    <LinearProgress variant="determinate" value={completionPct} sx={{ height: 8, borderRadius: 999 }} />
                  </Stack>
                </SectionCard>

                <Grid container spacing={1.6}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Employee Code"
                      fullWidth
                      required
                      sx={fieldSx}
                      value={employeeCode}
                      onChange={(e) => setEmployeeCode(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeRoundedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SearchableSelect
                      label="Project"
                      value={projectId}
                      onChange={setProjectId}
                      options={projects}
                      loading={projectsLoading}
                      getOptionLabel={(project) =>
                        project?.name ? `${project.name}${project.id ? ` (#${project.id})` : ''}` : String(project?.id || '')
                      }
                      getOptionValue={(project) => project?.id || ''}
                      helperText="Optional. Search by project name or ID."
                      textFieldProps={{ sx: fieldSx }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Device STGID"
                      fullWidth
                      required
                      sx={fieldSx}
                      value={deviceStgid}
                      onChange={(e) => setDeviceStgid(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DeviceHubRoundedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Log Time"
                      fullWidth
                      required
                      type="datetime-local"
                      sx={fieldSx}
                      value={logTime}
                      onChange={(e) => setLogTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Punch Type" fullWidth select required sx={fieldSx} value={type} onChange={(e) => setType(e.target.value)}>
                      {punchTypes.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Input Type" fullWidth select required sx={fieldSx} value={inputType} onChange={(e) => setInputType(e.target.value)}>
                      {inputTypes.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Temperature" fullWidth type="number" sx={fieldSx} value={temperature} onChange={(e) => setTemperature(e.target.value)} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Operation ID" fullWidth sx={fieldSx} value={operationId} onChange={(e) => setOperationId(e.target.value)} />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox checked={mask} onChange={(e) => setMask(e.target.checked)} />}
                      label="Wearing Mask"
                    />
                  </Grid>
                </Grid>

                <FormSection
                  title="Location"
                  description="Pick a point on the map or use the browser geolocation permission."
                  tone="dashboard"
                  contentSx={{ p: 0 }}
                >
                  <Grid container spacing={1.6}>
                    <Grid item xs={12} md={6}>
                      <TextField label="Latitude" fullWidth sx={fieldSx} value={lat} onChange={(e) => setLat(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Longitude" fullWidth sx={fieldSx} value={lng} onChange={(e) => setLng(e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <Button variant="outlined" startIcon={<GpsFixedRoundedIcon />} onClick={handleUseMyLocation}>
                        Use My Current Location
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      {apiKey ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: { xs: 320, md: 420 },
                            overflow: 'hidden',
                            borderRadius: theme.hr?.radius?.lg || 2,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <APIProvider apiKey={apiKey}>
                            <Map
                              style={{ width: '100%', height: '100%' }}
                              center={{ lat: mapLat, lng: mapLng }}
                              defaultCenter={DEFAULT_CENTER}
                              defaultZoom={DEFAULT_ZOOM}
                              onClick={handleMapClick}
                            >
                              <Marker position={{ lat: mapLat, lng: mapLng }} label="Attendance Location" />
                            </Map>
                          </APIProvider>
                        </Box>
                      ) : (
                        <Alert severity="warning">Google Maps API key is missing or invalid.</Alert>
                      )}
                    </Grid>
                  </Grid>
                </FormSection>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  <Button variant="outlined" onClick={() => navigate('/')} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button variant="contained" type="submit" startIcon={<SaveRoundedIcon />} disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Attendance'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </FormSection>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2} sx={{ position: { lg: 'sticky' }, top: { lg: 92 } }}>
            <SectionCard tone="dashboard" title="Record Summary" subheader="Review before submitting.">
              <Stack spacing={1}>
                <DetailField label="Employee" value={employeeCode} icon={<BadgeRoundedIcon fontSize="small" />} />
                <DetailField label="Project" value={selectedProject?.name || 'No project linked'} icon={<WorkOutlineRoundedIcon fontSize="small" />} />
                <DetailField label="Punch" value={type} icon={<AccessTimeRoundedIcon fontSize="small" />} />
                <DetailField label="Source" value={inputType} icon={<DeviceHubRoundedIcon fontSize="small" />} />
                <DetailField label="Coordinates" value={`${mapLat.toFixed(6)}, ${mapLng.toFixed(6)}`} icon={<MapRoundedIcon fontSize="small" />} />
              </Stack>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </PageLayout>
  );
}
